/*
 * Copyright (c) 2024-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, OnInit} from '@angular/core';
import {MomentumProgram} from '../../model/momentumProgram.model';
import {
    CellClassRules,
    ColDef,
    SizeColumnsToContentStrategy,
    SizeColumnsToFitGridStrategy,
    SizeColumnsToFitProvidedWidthStrategy
} from 'ag-grid-community';
import {UserSearchCellEditorComponent} from '../cell-renderer/user-search-cell-editor.component';
import {GpsTiming} from '../../model/gpsProgramTiming.model';
import {EditableGridComponent} from '../editable-grid-component';
import {GpsRpo} from '../../model/gps-rpo';
import {
    CustomMultiSelectFilterComponent
} from '../filter-components/custom-multi-select-filter/custom-multi-select-filter.component';
import {
    CustomMultiSelectFloatingFilterComponent
} from '../filter-components/custom-multi-select-filter/custom-multi-select-floating-filter.component';
import {ButtonCellRendererGpsComponent} from '../cell-renderer/button-cell-renderer-gps.component';

@Component({
    selector: 'app-gps-timing',
    templateUrl: './gps-timing.component.html',
    styleUrls: ['./gps-timing.component.scss'],
    standalone: false
})
export class GpsTimingComponent extends EditableGridComponent implements OnInit {
  permissionUrl = '/esciUI/gpsTimingViewPermission';
  adminPermissionUrl = '/esciUI/gpsTimingAdminPermission';
  getGpsRposUrl = '/esciUI/getGpsRpos/fromTiming';
  saveUrl = '/esciUI/saveGpsTiming';
  momentumPrograms: MomentumProgram[];
  gpsRpos: GpsRpo[] = [];
  missingGpsRpos: GpsRpo[] = [];
  rowData: GpsTiming[];
  gpsTimingFromDB: GpsTiming[];
  public autoSizeStrategy:
      | SizeColumnsToFitGridStrategy
      | SizeColumnsToFitProvidedWidthStrategy
      | SizeColumnsToContentStrategy = {
    type: 'fitGridWidth',
  };
  mrdCellClassRules: CellClassRules = {
    'green-cell': params => params.value && !this.isDateBeforeTodayPlusOffset(params.value),
    'red-cell': params => params.value && this.isDateBeforeTodayPlusOffset(params.value),
    'cell-changed': params => params.data.changes?.get(params.colDef.field)
  };

  dateCellClassRules: CellClassRules = {
    'green-cell': params => !this.isDateBeforeTodayPlusOffset(params.value, 90),
    'yellow-cell': params => this.isDateBetweenTodayAndOffset(params.value, 90),
    'red-cell': params => this.isDateBeforeTodayPlusOffset(params.value),
    'bold' : params => !!params.data.adjustedWeeks,
    'cell-changed': params => params.data.changes?.get(params.colDef.field)
  };

  emailCellClassRules: CellClassRules = {
    'green-cell': params => !this.isDateBeforeTodayPlusOffset(params.value, 14),
    'yellow-cell': params => this.isDateBetweenTodayAndOffset(params.value,  14),
    'red-cell': params => this.isDateBeforeTodayPlusOffset(params.value),
    'cell-changed': params => params.data.changes?.get(params.colDef.field)
  };
  vehicleStage3Rules: CellClassRules = {
    'orange-cell bold': params => this.isDateBeforeOtherDate(params, 'vehicleStage3Rcd', 'sixteenWeeksToMrd'),
    'cell-changed': params => params.data.changes?.get(params.colDef.field)
  };
  cols: ColDef[] = [
    {field: 'gpsRpo.type', headerName: 'GPS Type', pinned: 'left', editable: false, filter: CustomMultiSelectFilterComponent, floatingFilterComponent: CustomMultiSelectFloatingFilterComponent},
    {field: 'gpsRpo.programFamily', headerName: 'GPS Program Family', pinned: 'left', editable: false, filter: CustomMultiSelectFilterComponent, floatingFilterComponent: CustomMultiSelectFloatingFilterComponent},
    {field: 'momentumProgram.modelYear', headerName: 'Model Year', pinned: 'left', minWidth: 80, editable: false, filter: CustomMultiSelectFilterComponent, floatingFilterComponent: CustomMultiSelectFloatingFilterComponent},
    {field: 'gpsRpo.rpo', headerName: 'GPS RPO', pinned: 'left', minWidth: 110, editable: false, filter: CustomMultiSelectFilterComponent, floatingFilterComponent: CustomMultiSelectFloatingFilterComponent},
    {field: 'gpsRpo.controller', headerName: 'Controller', editable: false, filter: CustomMultiSelectFilterComponent, floatingFilterComponent: CustomMultiSelectFloatingFilterComponent},
    {field: 'gpsProgramClassification', headerName: 'GPS Program Classification', minWidth: 120, editable: false, filter: CustomMultiSelectFilterComponent, floatingFilterComponent: CustomMultiSelectFloatingFilterComponent},
    {field: 'vehicleProgramCode', headerName: 'Vehicle Program Code', minWidth: 300, editable: false},
    {field: 'vehicleStage3Rcd', headerName: 'Vehicle Stage 3 RCD', cellEditor: 'agDateCellEditor', cellDataType: 'date', cellClassRules: this.vehicleStage3Rules},
    {field: 'mrdType', headerName: 'MRD Type', minWidth: 100, editable: false, filter: CustomMultiSelectFilterComponent, floatingFilterComponent: CustomMultiSelectFloatingFilterComponent},
    {field: 'originalMrd', headerName: 'MRD Of Last Save (If Different)', cellClassRules: this.mrdCellClassRules, editable: false, cellEditor: 'agDateCellEditor', cellDataType: 'date',
    valueGetter: (params) => params.data.mrd?.getTime() !== params.data.originalMrd?.getTime() ? params.data['originalMrd'] : null
    },
    {field: 'mrd', headerName: 'MRD', cellClassRules: this.mrdCellClassRules, editable: false, cellEditor: 'agDateCellEditor', cellDataType: 'date'},
    {field: 'sixteenWeeksToMrd', headerClass: 'mrd-release-column-header', editable: false, cellClassRules: this.dateCellClassRules, headerName: 'Release Schematics\n (16 weeks to MRD)\n',
      cellRenderer: ButtonCellRendererGpsComponent,
      cellRendererParams: {
        dateField: 'sixteenWeeksToMrd',
        showButton: true,
        adjustmentField: 'adjustedWeeks',
      },
      minWidth: 200},
    {field: 'twentyOneWeeksToMrd', cellClassRules: this.dateCellClassRules, editable: false, headerName: 'Component DRE Stage 2 (Beta) & 3 (Gamma) Due Date(21 weeks before MRD)',
      cellRenderer: ButtonCellRendererGpsComponent,
      cellRendererParams: {
        dateField: 'twentyOneWeeksToMrd',
        adjustmentField: 'adjustedWeeks',
      },
      minWidth: 140},
    {field: 'twentyFiveWeeksToMrd', cellClassRules: this.dateCellClassRules, editable: false, headerName: 'Component DRE kickoff ICDe work for Stage 2 (Beta) & 3 (Gamma)\n (25 weeks to MRD)\n',
      cellRenderer: ButtonCellRendererGpsComponent,
      cellRendererParams: {
        dateField: 'twentyFiveWeeksToMrd',
        adjustmentField: 'adjustedWeeks',
      },
      minWidth: 140},
    {field: 'twentyNineWeeksToMrd', cellClassRules: this.dateCellClassRules, editable: false, headerName: 'List of required ICDes provided for this release (provided by EA based on IO matrix, Logicals and EMS drawing(from DSE)) (29 weeks to MRD)',
      cellRenderer: ButtonCellRendererGpsComponent,
      cellRendererParams: {
        dateField: 'twentyNineWeeksToMrd',
        adjustmentField: 'adjustedWeeks',
      },
      minWidth: 140},
    {field: 'thirtyOneWeeksToMrd', cellClassRules: this.dateCellClassRules, editable: false, headerName: 'Application specific IO Matrix Complete (Controller DSE originates)\n (31 weeks to MRD)\n',
      cellRenderer: ButtonCellRendererGpsComponent,
      cellRendererParams: {
        dateField: 'thirtyOneWeeksToMrd',
        adjustmentField: 'adjustedWeeks',
      },
      minWidth: 140},
    {
      field: 'gpsRpo.architect', headerName: 'Propulsion Electrical Architect', minWidth: 280,
      valueGetter: (params) => this.getValueFromUser(params.data['gpsRpo'], 'architect'),
      filterValueGetter: (params) => this.getValueFromUser(params.data['gpsRpo'], 'architect'),
      cellRenderer: UserSearchCellEditorComponent,
      cellRendererParams: {
        userField: 'gpsRpo.architect',
        readonly: true
      },
      editable: false
    },
    {
      field: 'gpsRpo.dse', headerName: 'Design System Engineer', minWidth: 280,
      valueGetter: (params) => this.getValueFromUser(params.data['gpsRpo'], 'dse'),
      filterValueGetter: (params) => this.getValueFromUser(params.data['gpsRpo'], 'dse'),
      cellRenderer: UserSearchCellEditorComponent,
      cellRendererParams: {
        userField: 'gpsRpo.dse',
        readonly: true
      },
      editable: false
    },
    {field: 'released', headerName: 'Schematic Released'},
    {field: 'dynoRequired', headerName: 'Dyno Harness Build Required'},
    {field: 'fourWeekNotificationEmail', editable: false, headerName: '4 Week Notification Email', cellClassRules: this.emailCellClassRules},
    {field: 'twoWeekNotificationEmail', editable: false, headerName: '2 Week Notification Email', cellClassRules: this.emailCellClassRules},
    {field: 'oneWeekNotificationEmail', editable: false, headerName: '1 Week Notification Email', cellClassRules: this.emailCellClassRules},
    {field: 'oneDayLateEscalationEmail', editable: false, headerName: '1 Day Late Escalation Email', cellClassRules: this.emailCellClassRules},
    {field: 'notes', headerName: 'Notes'},
    {field: 'privateNotes', headerName: 'Private Notes'},
  ];
  adminOnlyColumns = ['privateNotes'];

  ngOnInit(): void {
    this.cols.forEach(col => {
      col.tooltipValueGetter = (params) => {
        return params.data.changes.get(col.field);
      };
    });
    this.permission.set(false);
    this.esciService.getPermissionAndFileSizeLimit(this, false);
    this.esciService.getGpsRpos(this, () => {
          // get gps timing data, then get momentum data, then populate grid data and hide admin columns
          this.esciService.getGpsTiming(this,
              () => this.esciService.getGpsMomentumData(this,
                  () => {
                    this.populateGridData();
                    this.setColumnsVisible();
                  }));
        }
    );
    // The custom button cell renderers in the ag-grid call the esci-service to notify us of a change to recalculate
    this.esciService.buttonClick$.subscribe((params) => {
      params.data.calculateDates();
      // Mark dirty immediately on button click so Save enables on the first adjustment.
      params.data.state = 'changed';
      this.dataChanged = true;
      this.duplicate = false;
      this.rowsInvalid = false;
      // Compute row/cell change markers immediately so red styling is not one click behind.
      this.updateDataChanged(this.rowData);
      if (params.node && this.gridApi && !this.gridApi.isDestroyed()) {
        this.gridApi.refreshCells({rowNodes: [params.node], force: true});
        this.gridApi.redrawRows({rowNodes: [params.node]});
      }
      this.refreshGrid(this.rowData, params.node);
    });
  }
  // this is where we combine the momentum data with our gps rpo data to make our gpsTiming data
  populateGridData() {
    if (this.adminPermission() && this.missingGpsRpos?.length) {
      const sortedRpos = Array.from(this.missingGpsRpos).map(o => o.rpo).sort((a, b) => a.localeCompare(b));
      // popup a notification to user stating that there are missing rpos
      // display rpos as comma separated list
      this.confirmationService.confirm({
        message: `The following RPOs were found in Momentum Programs but not in your list of GPS RPOs. <br/>
                      You must add them in the GPS RPO Management Page in order for timings to appear. <br/><br/>
                      <b>Missing RPOs:</b> ${sortedRpos.join(', ')}`,
        header: 'Missing RPOs',
        key: 'missingGpsRposConfirmation',
        icon: 'pi pi-exclamation-triangle',
        acceptButtonStyleClass: 'p-button-success',
        rejectIcon: 'pi pi-check mr-2',
        acceptIcon: 'pi pi-external-link mr-2',
        rejectLabel: 'Ignore',
        acceptLabel: 'Add Them Now',
        accept: () => {
          // navigate to management page but send the list of missing rpos so they can be auto-added
          this.router.navigate(['gpsRpoManagement']);
        }
      });
    }
    this.setRowData(new Array<GpsTiming>());
    this.gpsRpos.forEach(gpsRpo => {
      this.momentumPrograms.forEach(momentumProgram => {
        if (momentumProgram.gpsRpo === gpsRpo.rpo) {
          const gpsProgramTiming = new GpsTiming();
          gpsProgramTiming.gpsRpo = gpsRpo;
          gpsProgramTiming.momentumProgram = momentumProgram;
          gpsProgramTiming.setGpsTimingDates();
          gpsProgramTiming.gpsRpoId = gpsRpo.id;
          gpsProgramTiming.modelYear = momentumProgram.modelYear;
          gpsProgramTiming.gpsProgramClassification = momentumProgram.programClassification;
          gpsProgramTiming.gpsType = gpsRpo.type;
          gpsProgramTiming.gpsProgramFamily = gpsRpo.programFamily;
          gpsProgramTiming.vehicleProgramCode = momentumProgram.programCode;
          gpsProgramTiming.adjustedWeeks = 0;
          gpsProgramTiming.hasAdminPermission = !!this.adminPermission();
          this.rowData.push(gpsProgramTiming);
        }
      });
    });
    // combine like rows where the following are the same:
    // gps type, gps program family, rpo, mrd type, model year, gps program classification
    // and concatenate program code with commas
    const uniqueRows = new Array<GpsTiming>();
    this.rowData.forEach(row => {
      const existingRow = uniqueRows.find(r => r.gpsRpo.type === row.gpsRpo.type
          && r.gpsRpo.programFamily === row.gpsRpo.programFamily
          && r.gpsRpo.rpo === row.gpsRpo.rpo
          && r.mrdType === row.mrdType
          && r.modelYear === row.modelYear
          && r.gpsProgramClassification === row.gpsProgramClassification
      );
      if (existingRow) {
        // only concatenate the program code if that program code is not already in the row
        // because many codes are similar, cannot just do includes
        if (!existingRow.vehicleProgramCode.includes(',' + row.vehicleProgramCode) &&
            !existingRow.vehicleProgramCode.includes(row.vehicleProgramCode + ',') &&
             existingRow.vehicleProgramCode !== row.vehicleProgramCode) {
          existingRow.vehicleProgramCode += `,${row.vehicleProgramCode}`;
        }
      } else {
        uniqueRows.push(row);
      }
    });
    this.setRowData(uniqueRows);

    // combine the data from db to populate the dyno and release fields
    if (this.gpsTimingFromDB) {
      this.gpsTimingFromDB.forEach(dbRow => {
        const row = this.rowData.find(r => r.gpsRpoId === dbRow.gpsRpoId
            && r.momentumProgram.modelYear === dbRow.modelYear
            && r.gpsProgramClassification === dbRow.gpsProgramClassification
            && r.mrdType === dbRow.mrdType
            && r.gpsType === dbRow.gpsType
            && r.gpsProgramFamily === dbRow.gpsProgramFamily
            && r.momentumProgram.programCode === dbRow.vehicleProgramCode
        );
        if (row) {
          row.released = dbRow.released;
          row.dynoRequired = dbRow.dynoRequired;
          row.notes = dbRow.notes;
          row.vehicleStage3Rcd = dbRow.vehicleStage3Rcd;
          row.adjustedWeeks = dbRow.adjustedWeeks;
          // // only need to show an "original" mrd if the mrd has changed since last save
          row.originalMrd = dbRow.originalMrd;
          if (this.adminPermission()) {
            // these shouldn't come back without admin permission but want to be sure here
            row.privateNotes = dbRow.privateNotes;
          }
        }
      });
    }
    // now we have all data and we can calculate our dates
    this.rowData.forEach(row => {
      row.calculateDates();
      row.originalRowData = structuredClone(row);
    });
    // sort on mrd date
    this.rowData.sort((a, b) => {
        return new Date(a.mrd).getTime() - new Date(b.mrd).getTime();
    });
    this.loading = false;
    this.resetting = false;
    this.exporting = false;
  }

  exportReport() {
    // export all data in the grid
    this.excelService.exportGridDataAsExcelFile(this.gridApi, 'GPS_Timing_Report');
  }

  private isDateBeforeTodayPlusOffset(firstDate: Date, offset?: number) {
    if (!firstDate) {
      return false;
    }
    const secondDate = new Date();
    if (offset) {
      secondDate.setDate(secondDate.getDate() + offset);
    }
    return firstDate < secondDate;
  }
  private isDateBetweenTodayAndOffset(firstDate: Date, offset: number) {
    return !this.isDateBeforeTodayPlusOffset(firstDate)
        && this.isDateBeforeTodayPlusOffset(firstDate, offset);
  }
  private isDateBeforeOtherDate(params: any, date1String: string, date2String: string) {
    const date1 = params.data[date1String];
    const date2 = params.data[date2String];
    if (date1 && date2) {
      return date1 < date2;
    }
    return false;
  }

  resetChanges() {
    this.resetting = true;
    this.exporting = true;
    this.messageService.clear();
    this.esciService.getGpsMomentumData(this, () => this.populateGridData());
  }

  saveGpsTimingRows() {
    if (this.areRowsValid(this.rowData) && this.adminPermission()) {
      this.saving = true;
      // split combined rows back out into one row per program code
      // otherwise, the user data like stage 3 rd won't get saved to all rows, just one
      const fullRows = new Array<GpsTiming>();
      this.rowData.forEach(row => {
        // here, each row has all program codes combined
        const programCodes = row.vehicleProgramCode.split(',');
        programCodes.forEach(code => {
          const newRow = structuredClone(row);
          newRow.vehicleProgramCode = code;
          fullRows.push(newRow);
        });
      });
      this.esciService.saveRows(this, fullRows, 'GPS Timing', () => {
        for (const row of this.rowData) {
          row.changes.clear();
          row.state = 'saved';
          row.originalRowData = structuredClone(row);
        }
        // Do not call refreshGrid here: replacing rowData makes AG Grid rebuild the client-side
        // row model, which can reorder or temporarily drop filtered rows (e.g. MRD date filter).
        // Do not call updateDataChanged here: it JSON-stringifies row vs structuredClone(originalRowData)
        // and false-positives on GpsTiming (class instances vs plain objects, dates), re-marking cells as changed.
        this.dataChanged = false;
        if (this.gridApi && !this.gridApi.isDestroyed()) {
          this.gridApi.refreshCells({force: true});
          this.gridApi.redrawRows();
        }
      });
    }
  }

  // Default the MRD Filter to only values after today minus one week
  setFilter() {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    const formattedDate = date.toISOString().split('T')[0];
    const model = {
      'dateFrom': formattedDate,
      'type': 'greaterThan'
    };
    this.gridApi.setColumnFilterModel('mrd', model).then(r => {
        this.gridApi.onFilterChanged();
    });
  }
}
